import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { retry } from 'redux-saga/effects'
import { CourseStateType } from 'enum'
import { StateCourse } from '../types'
import { ShowDates } from '../constants'

const CourseStateTypeKeys = [
  CourseStateType.Draft,
  CourseStateType.Published,
  CourseStateType.Soon,
  CourseStateType.Imparted,
  CourseStateType.Inactive,
]

interface UseStateOptionsProps {
  untilDate?: Date
  showDates: ShowDates
  hasLessons: boolean
  isSynchronous: boolean
  hasExternalIntegration: boolean
}

export const useStateOptions = ({
  untilDate,
  showDates,
  hasLessons,
  isSynchronous,
  hasExternalIntegration,
}: UseStateOptionsProps) => {
  const { t } = useTranslation()

  const startOfToday = moment().startOf('day')
  const courseHasEnded = untilDate
    ? moment(untilDate).isBefore(startOfToday) && showDates === ShowDates.active
    : false

  const getLabel = (key: CourseStateType) =>
    t(`commons.course.state.${CourseStateType[key]}`)

  const buildOption = (value: CourseStateType) => ({
    value: value,
    label: getLabel(value),
  })

  const stateOptionsBase = useMemo<StateCourse[]>(
    () => CourseStateTypeKeys.map((value) => buildOption(value)),
    [CourseStateTypeKeys, t]
  )

  const publishedErrors = useMemo(() => {
    const errors = []
    const prefix = 'administration.courses.new.form.validations'
    if (courseHasEnded) errors.push(t(`${prefix}.courseHasEnded`))
    if (!hasLessons && !isSynchronous && !hasExternalIntegration)
      errors.push(t(`${prefix}.disabledPublished`))
    return errors
  }, [hasLessons, isSynchronous, courseHasEnded, hasExternalIntegration])

  const draftOption = useMemo(() => buildOption(CourseStateType.Draft), [])
  const importedOption = useMemo(
    () => buildOption(CourseStateType.Imparted),
    []
  )

  const stateOptions = useMemo<StateCourse[]>(
    () => [
      ...stateOptionsBase
        .filter((option) => {
          if (option.value === CourseStateType.Imparted) return courseHasEnded
          return true
        })
        .map((option) => {
          if (option.value === CourseStateType.Published)
            return {
              ...option,
              disabled: publishedErrors.length > 0,
            }
          if (option.value === CourseStateType.Soon)
            return {
              ...option,
              disabled: hasExternalIntegration ? true : courseHasEnded,
            }
          return option
        }),
    ],
    [
      stateOptionsBase,
      hasLessons,
      isSynchronous,
      hasExternalIntegration,
      courseHasEnded,
    ]
  )

  return {
    stateOptions,
    stateOptionsBase,
    courseHasEnded,
    publishedErrors,
    draftOption,
    importedOption,
  }
}
